import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';

import type { W_Employee_FindById_VM } from 'ab-api/web/employee/findById';

import type { EmployeeStatusHistoryTableViewModel } from 'ab-viewModels/employeeStatusHistoryTable.viewModel';
import type * as ResourceStatusesViewModel from 'ab-viewModels/resources/resourceStatuses.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { TimePeriodRecurrence } from '@acceligentllc/shared/enums/timePeriodRecurrence';

import type { RootState } from 'af-reducers';

import { bemElement } from 'af-utils/bem.util';
import { downloadCSV } from 'af-utils/csv.utils';
import * as SettingsUtils from 'af-utils/settings.util';

import BrowserStorageEnum from 'ab-enums/browserStorage.enum';
import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import * as SettingsKeys from 'af-constants/settingsKeys';

import { ALL_STATUSES_OPTION_ID } from 'ab-common/constants/value';
import type { TableContent } from 'ab-common/dataStructures/tableContent';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import Breadcrumbs from 'af-components/Breadcrumbs';
import Dropdown from 'af-components/Controls/Dropdown';
import DateFilter from 'af-components/DateFilter';
import LockedValue from 'af-components/LockedValue';
import RectangleButton from 'af-components/MultipleOptionsButton/RectangleButton';
import StatusLabel from 'af-components/StatusLabel';
import Table from 'af-components/Table6';
import type { TabProps, Column } from 'af-components/Table6';
import EmptyCell from 'af-components/Table6/Cells/EmptyCell';
import type TableComponent from 'af-components/Table6/Table';

import CLIENT from 'af-routes/client';

import * as EmployeeActions from 'af-actions/employee';

import { toCSVData } from './helpers';

const ALL_STATUSES_OPTION: ResourceStatusesViewModel.Item = {
	name: 'All Statuses',
	id: ALL_STATUSES_OPTION_ID,
	index: ALL_STATUSES_OPTION_ID,
	available: false,
	isDeleted: false,
};

interface PathParams {
	id: string;
}

interface SettingProps {
	startDate: Date;
	endDate: Date;
	period: TimePeriodRecurrence;
	textFilter: string;
}

type OwnProps = CustomRouteComponentProps<PathParams>;

const connector = connect(mapStateToProps, mapDispatchToProps());

type Props = OwnProps & SettingProps & ConnectedProps<typeof connector>;

interface State {
	startDate: Date;
	endDate: Date;
	period: TimePeriodRecurrence;
	textFilter: string;
	statuses: ResourceStatusesViewModel.Item[];
	filterByStatus: ResourceStatusesViewModel.Item;
	employee: Nullable<W_Employee_FindById_VM>;
}

const setLocalStorageStartDate = (date: Date = new Date()) => {
	SettingsUtils.setItemWithFormatter(
		SettingsKeys.EMPLOYEE_STATUS_HISTORY_START_DATE(),
		date,
		(value: Date) => TimeUtils.formatDate(value, TimeFormat.FULL_DATE),
		BrowserStorageEnum.LOCAL_STORAGE
	);
};

const setLocalStorageEndDate = (date: Date = new Date()) => {
	SettingsUtils.setItemWithFormatter(
		SettingsKeys.EMPLOYEE_STATUS_HISTORY_END_DATE(),
		date,
		(value: Date) => TimeUtils.formatDate(value, TimeFormat.FULL_DATE),
		BrowserStorageEnum.LOCAL_STORAGE
	);
};

const setLocalStoragePeriod = (period: TimePeriodRecurrence) => {
	SettingsUtils.setItem(SettingsKeys.EMPLOYEE_STATUS_HISTORY_PERIOD(), period, BrowserStorageEnum.LOCAL_STORAGE);
};

class EmployeeStatusHistory extends React.PureComponent<Props, State> {

	state: State = {
		startDate: this.props.startDate,
		endDate: this.props.endDate,
		period: this.props.period,
		textFilter: this.props.textFilter,
		statuses: [] as ResourceStatusesViewModel.Item[],
		filterByStatus: {} as ResourceStatusesViewModel.Item,
		employee: null,
	};

	private _tableRef: Nullable<TableComponent<EmployeeStatusHistoryTableViewModel>> = null;

	private _findStatusHistoryForEmployee: (
		table: TableQuery,
		startDate: Date,
		endDate: Date,
		filterByStatusId: number,
	) => Promise<TableContent<EmployeeStatusHistoryTableViewModel>>;

	columns: Column<EmployeeStatusHistoryTableViewModel>[] = [
		{
			Header: 'Status',
			sortable: false,
			accessor: 'employeeStatus',
			// status label
			Cell: ({ original }) => <StatusLabel isAvailable={original.status?.available ?? true} label={original.status?.name ?? 'Available'} statusLabelClassname="__bold" />,
		},
		{
			Header: 'Status Valid',
			accessor: 'statusValid',
			sortable: false,
			Cell: ({ original }) => (
				<div className="locked-input-one-row">
					<LockedValue label="from " labelClassName="locked-input-one-row__header" value={original.statusValidFrom} valueClassName="locked-input-one-row__content"></LockedValue>
					{
						original.statusValidTo &&
						<LockedValue label="to " labelClassName="locked-input-one-row__header" value={original.statusValidTo} valueClassName="locked-input-one-row__content"></LockedValue>
					}

				</div>
			),
		},
		{
			Header: 'Unavailability Reason',
			accessor: 'unavailabilityReason',
			sortable: false,
			Cell: ({ original }) => (original.status?.available ?? true) ? <EmptyCell /> : (original.unavailabilityReason ?? <EmptyCell message="None" />),
		},
	];

	constructor(props: Props) {
		super(props);
		const { findStatusHistoryForEmployee, match: { params: { id } } } = this.props;
		this._findStatusHistoryForEmployee = findStatusHistoryForEmployee.bind(this, id);
	}

	findAllForStatusHistoryTable = async (tableRequestModel: TableQuery) => {
		const { startDate, endDate, filterByStatus } = this.state;

		return await this._findStatusHistoryForEmployee(tableRequestModel, startDate, endDate, filterByStatus.id);
	};

	onTableMount = (table: TableComponent<EmployeeStatusHistoryTableViewModel>) => {
		this._tableRef = table;
	};

	refresh = () => {
		if (this._tableRef) {
			this._tableRef.refreshTable();
		}
	};

	filterByDate = (startDate: Date, endDate: Date) => {
		this.setState(
			() => ({ startDate, endDate }),
			() => {
				setLocalStorageStartDate(startDate);
				setLocalStorageEndDate(endDate);
				this.refresh();
			}
		);
	};

	changePeriod = (period: TimePeriodRecurrence, selected: Date) => {
		let startDate: Date = selected, endDate: Date = selected;

		switch (period) {
			case TimePeriodRecurrence.MONTHLY:
				startDate = TimeUtils.positionDate(
					TimeUtils.positionDate(selected, 'start', 'month'),
					'start',
					'day'
				);
				endDate = TimeUtils.positionDate(
					TimeUtils.positionDate(selected, 'end', 'month'),
					'end',
					'day'
				);
				break;
			case TimePeriodRecurrence.WEEKLY:
				startDate = TimeUtils.positionDate(
					TimeUtils.positionDate(selected, 'start', 'week'),
					'start',
					'day'
				);
				endDate = TimeUtils.positionDate(
					TimeUtils.positionDate(selected, 'end', 'week'),
					'end',
					'day'
				);
				break;
			case TimePeriodRecurrence.DAILY:
			case TimePeriodRecurrence.CUSTOM:
				startDate = TimeUtils.positionDate(selected, 'start', 'day');
				endDate = TimeUtils.positionDate(selected, 'end', 'day');
			default:
				break;
		}

		this.setState(
			() => ({ period, startDate, endDate }),
			() => {
				setLocalStoragePeriod(period);
				setLocalStorageStartDate(startDate);
				setLocalStorageEndDate(endDate);
				this.refresh();
			}
		);
	};

	filterByStatus = async (option: ResourceStatusesViewModel.Item) => {
		this.setState(
			() => ({ filterByStatus: option || {} as ResourceStatusesViewModel.Item }),
			() => {
				if (this._tableRef) {
					this._tableRef.refreshTable();
				}
			}
		);
	};

	changeFilterText = (textFilter: string) => {
		this.setState(
			() => ({ textFilter }),
			() => {
				this.refresh();
			}
		);
	};

	onDownloadCSVClick = async () => {
		const {
			match: { params: { id } },
			companyData: { name: companyName },
		} = this.props;

		const tableRequestModel = new TableQuery({
			pageSize: 100,
			page: 0,
			sortBy: [{ id: 'returnDate', desc: true }],
			filterByText: this.state.textFilter ?? '',
			includeAll: true,
		});
		const listViewModel = await this.findAllForStatusHistoryTable(tableRequestModel);
		downloadCSV(toCSVData(listViewModel.rows), `${companyName}_employee_${id}.csv`);
	};

	static renderStatusMenuItem = (optionItem: ResourceStatusesViewModel.Item) => !optionItem ? <></> : <div>{optionItem.name}</div>;

	renderFilter = () => {
		const { endDate, startDate, period, statuses } = this.state;

		return (
			<div className="table-filter field-report-orders-table__filters">
				<div className={bemElement('table-filter', 'parameter', ['no-margin-top', 'margin-right'])}>
					<Dropdown<ResourceStatusesViewModel.Item>
						className="field-report-orders-table__filter-button"
						defaultValue={ALL_STATUSES_OPTION}
						id="employee-statuses-history-filter"
						labelKey="name"
						onValueChange={this.filterByStatus}
						options={[ALL_STATUSES_OPTION, ...statuses]}
						renderMenuItem={EmployeeStatusHistory.renderStatusMenuItem}
						valueKey="id"
						withBorder={false}
					/>
					<RectangleButton
						action={undefined}
						isLeftOpen={true}
						isSquare={true}
						label={<span className="icon-filter" />}
					/>
				</div>
				<DateFilter
					changePeriod={this.changePeriod}
					endDate={endDate}
					onChange={this.filterByDate}
					period={period}
					startDate={startDate}
				/>
			</div>
		);
	};

	async componentDidMount() {
		const { findAllStatusesForEmployee, findEmployeeById, match: { params: { id } } } = this.props;
		const employee = await findEmployeeById(+id);
		const statuses = await findAllStatusesForEmployee(id);
		const extractedStatuses = [...statuses.available, ...statuses.unavailable];
		this.setState(() => ({ employee, statuses: extractedStatuses }));
	}

	tabs = (): TabProps<EmployeeStatusHistoryTableViewModel>[] => {
		return [
			{
				label: 'Employee Status History',
				columns: this.columns,
				buttons: [
					{
						type: TableButtonType.EXPORT,
						hasPermission: true,
						onClick: this.onDownloadCSVClick,
					},
				],
				hasSearchInput: true,
				searchLabel: 'Employee Status History',
				additionalFilter: this.renderFilter,
				fetch: this.findAllForStatusHistoryTable,
			},
		];
	};

	breadcrumbs = () => {
		const { location: { state: { orgAlias } }, companyData: { name: companyName } } = this.props;
		const { employee } = this.state;

		return [
			{ label: 'Labor', url: CLIENT.COMPANY.RESOURCES.EMPLOYEE.LIST(orgAlias, companyName) },
			{ label: `Status History: ${employee?.fullName}` },
		];
	};

	render() {
		const { textFilter } = this.state;

		return (
			<div className="form-segment form-segment--maxi">
				<Breadcrumbs items={this.breadcrumbs()} />
				<Table
					onFilterTextChange={this.changeFilterText}
					onMount={this.onTableMount}
					tableName={TableNameEnum.EMPLOYEE_STATUS_HISTORY}
					tabs={this.tabs()}
					textFilter={textFilter}
				/>
			</div>
		);
	}

}

function mapStateToProps(state: RootState) {
	const { companyData, userData } = state.user;
	if (!companyData || !userData) {
		throw new Error('User not logged in');
	}

	return {
		companyData,
		userData,
	};
}

function mapDispatchToProps() {
	return {
		findEmployeeById: EmployeeActions.findById,
		findStatusHistoryForEmployee: EmployeeActions.findStatusHistoryForEmployee,
		findAllStatusesForEmployee: EmployeeActions.findAllStatusesForEmployee,
	};
}

const normalizeDateToDate = (item: string) => {
	return TimeUtils.normalizeDateToDate(item, TimeFormat.FULL_DATE);
};
const enhance = compose<React.ComponentClass>(
	SettingsUtils.withSettings<SettingProps>(() => ([
		{
			key: SettingsKeys.EMPLOYEE_STATUS_HISTORY_START_DATE(),
			mappedName: 'startDate',
			normalize: normalizeDateToDate,
			defaultValue: new Date(),
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
		{
			key: SettingsKeys.EMPLOYEE_STATUS_HISTORY_END_DATE(),
			mappedName: 'endDate',
			normalize: normalizeDateToDate,
			defaultValue: new Date(),
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
		{
			key: SettingsKeys.EMPLOYEE_STATUS_HISTORY_PERIOD(),
			mappedName: 'period',
			defaultValue: TimePeriodRecurrence.DAILY,
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
	])),
	withRouter,
	connector
);

export default enhance(EmployeeStatusHistory);
